/**
 * @file Resolves the Form Derive row projections that fill this ArTable and
 * publishes them on the table's UI state, where `ArCell`, `ArRow`,
 * `useRowDeleteTargets`, `ArTableControls` and `useArTableActions` read them —
 * none of which is reachable by props from here. Called once, by `ArTableBody`.
 *
 * The write is an idempotent render-phase sync, exactly like `syncRenderedIds`
 * beside it: `uiState` can live inside `formStore.state.tables`, so an
 * unconditional write would bump the form store's version on every table render.
 * The PDF-export body mounts a second time over the same slot and computes the
 * same value, which the equality check turns into a no-op.
 *
 * Labels come from `formDeriveLabelResolvers`, the same body the settings drawer
 * uses, so a projected cell and the drawer never word one derive two ways. Only
 * the source tables of projections filling THIS table are subscribed to, never the
 * whole `tableTypes` collection. `referenceLabel` answers `undefined` here: the
 * key on a `rowReferenceProp` names a reference on the RECORD, which no form-level
 * registry can place, and an unplaceable id renders raw by contract.
 *
 * Outside a form there is no FormType and so no projection. A table rendered
 * from an entity record inside a form page does see the form's config, and that
 * is harmless because every consumer keys off the `drv~` row-id namespace, which
 * only exists in the collection the reconciler wrote.
 */

import { useContext, useMemo } from 'react';
import { useSnapshot } from 'valtio';

import { formStore } from '@/store';
import { useStoreDocsByPaths } from '@/store/useStoreDocsByPaths';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { useCompositeFieldTypes } from '@/components/CompositeField/useCompositeFieldTypes';
import { formDeriveLabelResolvers } from '@/services/formDerives';
import { createFormDeriveEngineConfig } from '#types';
import type { ArTableId, ArTableType } from '#types';
import type { ArTableUIState } from '../state/createArTableState';
import {
  resolveArTableDeriveProjections,
  type ArTableDeriveProjection,
  type ArTableDeriveProjectionColumn,
} from './resolveArTableDeriveProjections';

const tableTypePath = (tableId: ArTableId): string => `tableTypes/${tableId}`;

const columnsMatch = (
  current: readonly ArTableDeriveProjectionColumn[],
  next: readonly ArTableDeriveProjectionColumn[],
): boolean =>
  current.length === next.length &&
  current.every((column, index) => {
    const candidate = next[index];
    return column.typeId === candidate.typeId
      && column.subFieldId === candidate.subFieldId
      && column.sourcePhrase === candidate.sourcePhrase;
  });

/** Field-wise, because the records are flat and the array is normally empty or single. */
const projectionsMatch = (
  current: readonly ArTableDeriveProjection[],
  next: readonly ArTableDeriveProjection[],
): boolean =>
  current.length === next.length &&
  current.every((projection, index) => {
    const candidate = next[index];
    return projection.deriveId === candidate.deriveId
      && projection.sourceTable === candidate.sourceTable
      && projection.rowDeleteBlocked === candidate.rowDeleteBlocked
      && columnsMatch(projection.columns, candidate.columns);
  });

function useArTableDeriveProjections(params: {
  tableId: ArTableId;
  uiState: ArTableUIState;
}): ArTableDeriveProjection[] {
  const { tableId, uiState } = params;
  const { fieldTypes } = useContext(FieldTypeContext);
  const compositeFieldTypes = useCompositeFieldTypes();
  const formTypeSnap = useSnapshot(formStore.state.formType);
  const formDerives = formTypeSnap.data?.formDerives;
  const formDeriveRev = formTypeSnap.data?.formDeriveRev;

  const config = useMemo(
    () => createFormDeriveEngineConfig({ formDerives, formDeriveRev }),
    [formDerives, formDeriveRev],
  );

  /**
   * The source tables of the projections filling this one — one document each, for
   * the table and column labels their sentences quote. Empty for every table no
   * projection fills, which is nearly all of them.
   */
  const sourceTableIds = useMemo(() => [...new Set(
    config.entries.flatMap((entry) => {
      if (!('columns' in entry)) return [];
      return entry.target.tableId === tableId ? [entry.source.tableId] : [];
    }),
  )], [config.entries, tableId]);

  const sourceTablePaths = useMemo(() => sourceTableIds.map(tableTypePath), [sourceTableIds]);
  const { data: sourceTableDocs } = useStoreDocsByPaths<ArTableType>(sourceTablePaths);

  const sourceTableTypes = useMemo(() => sourceTableIds.flatMap((sourceTableId) => {
    const data = sourceTableDocs.get(tableTypePath(sourceTableId));
    return data === undefined ? [] : [{ ...data, docId: sourceTableId }];
  }), [sourceTableDocs, sourceTableIds]);

  const projections = useMemo(
    () => resolveArTableDeriveProjections({
      tableId,
      entries: config.entries,
      labels: formDeriveLabelResolvers({
        tableTypes: sourceTableTypes,
        fieldTypes,
        compositeFieldTypes,
        referenceLabel: () => undefined,
      }),
    }),
    [compositeFieldTypes, config.entries, fieldTypes, sourceTableTypes, tableId],
  );

  if (!projectionsMatch(uiState.deriveProjections, projections)) {
    uiState.deriveProjections.length = 0;
    uiState.deriveProjections.push(...projections);
  }

  return projections;
}

export { useArTableDeriveProjections };
